import {
  addMessageToThread,
  registerForUpdates,
  unregisterFromUpdates,
} from '../store/endpoints/threads';
import { AsyncResult, resultFactory } from '../utils/async-result';
import { ConversationThreadId, WidgetId } from '../models/aliases';
import { getAtOffset, lastOf, lengthOf } from '../utils/speculated-array';
import { loadResultSet } from '../store/endpoints/result-set';
import { loadWidgetState } from '../store/endpoints/widget-state';
import {
  PaperFinderWidgetAction,
  WidgetActionsDispatcher,
} from '../models/widget-actions';
import { RootState } from '../../../../store/store';
import {
  selectPaperFinderAllMessages,
  selectPaperFinderWidgetState,
  selectWidgetCache,
} from '../store/selectors/ui-state';
import { StateUpdateMessage, WidgetActionMessage } from '../models/threads';
import { useAppDispatch } from '../../../../store/hooks';
import { useVisibilityEffect } from './useVisibilityEffect';
import { WidgetClientState } from '../models/widget-state';

import { useSelector } from 'react-redux';

interface PaperFinderDataResult {
  stateResult: AsyncResult<WidgetClientState | undefined>;
  dispatchWidgetAction: (action: PaperFinderWidgetAction) => void;
}

export function usePaperFinderData(
  baseUrl: string,
  threadId?: ConversationThreadId,
  widgetId?: WidgetId,
): PaperFinderDataResult {
  const dispatch = useAppDispatch();
  const allMessagesResult = useSelector((state: RootState) =>
    selectPaperFinderAllMessages(state, threadId),
  );
  const widgetCaches = useSelector((state: RootState) =>
    selectWidgetCache(state, threadId, widgetId),
  );
  const widgetStateResult = useSelector((state: RootState) =>
    selectPaperFinderWidgetState(state, threadId, widgetId),
  );

  useVisibilityEffect(() => {
    threadId && dispatch(registerForUpdates({ threadId, baseUrl }));
    return () => {
      threadId && dispatch(unregisterFromUpdates(threadId));
    };
  }, [threadId]);

  if (
    threadId === undefined ||
    !allMessagesResult.isSuccess ||
    lengthOf(widgetCaches.stateIndexes) === 0
  ) {
    return {
      stateResult: resultFactory.uninitialized(),
      dispatchWidgetAction: () => {},
    };
  }

  if (
    widgetStateResult.isSuccess &&
    widgetStateResult.data?.roundStatus.kind === 'failed'
  ) {
    const message =
      widgetStateResult.data?.roundStatus.error ?? 'Unknown Error';
    return {
      stateResult: resultFactory.failed({ name: 'Server Error', message }),
      dispatchWidgetAction: () => {},
    };
  }

  const lastStateMessageOffset = lastOf(widgetCaches.stateIndexes);
  const lastStateMessage = getAtOffset(
    allMessagesResult.data,
    lastStateMessageOffset,
  ) as StateUpdateMessage;

  if (lastStateMessage !== undefined) {
    dispatch(
      loadWidgetState({
        baseUrl,
        stateId: lastStateMessage.stateId,
      }),
    );

    if (lastStateMessage.status === 'completed') {
      dispatch(
        loadResultSet({
          baseUrl,
          widgetId: lastStateMessage.widgetId,
        }),
      );
    }
  }

  const dispatchWidgetAction: WidgetActionsDispatcher = (action) => {
    if (allMessagesResult.isSuccess) {
      const message: WidgetActionMessage = {
        kind: 'action',
        action,
        offsetInThread: lengthOf(allMessagesResult.data),
        sender: {
          id: '1',
          displayName: 'client',
        },
        predecessorOffsetInThread: lastStateMessageOffset,
      };

      dispatch(addMessageToThread({ threadId, baseUrl, message }));
    }
  };

  return { stateResult: widgetStateResult, dispatchWidgetAction };
}
